import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Navbar } from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import { Checkbox } from '../components/ui/checkbox';
import { Skeleton } from '../components/ui/skeleton';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Download,
  Edit2,
  Trash2,
  RefreshCw,
  Save,
  X,
  FileText,
  CheckCircle2,
  Quote,
  Lightbulb,
  Loader2,
  Copy,
  BookmarkPlus,
  GraduationCap,
  Plus,
  Eye,
  EyeOff
} from 'lucide-react';
import { Switch } from '../components/ui/switch';
import PassageReader from '../components/PassageReader';
import MathText from '../components/MathText';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const QuizReviewPage = () => {
  const { quizId } = useParams();
  const { getAuthHeader } = useAuth();
  const navigate = useNavigate();

  const [quiz, setQuiz] = useState(null);
  const [lesson, setLesson] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingIndex, setEditingIndex] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [regeneratingIndex, setRegeneratingIndex] = useState(null);
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportOptions, setExportOptions] = useState({
    format: 'pdf',
    includeAnswers: true,
    includeExplanations: true,
    includePassage: false
  });
  const [exporting, setExporting] = useState(false);
  
  // Save as Template state
  const [showSaveTemplateModal, setShowSaveTemplateModal] = useState(false);
  const [templateForm, setTemplateForm] = useState({
    name: '',
    description: ''
  });
  const [savingTemplate, setSavingTemplate] = useState(false);

  // Standards state
  const [showStandardsModal, setShowStandardsModal] = useState(false);
  const [allStandards, setAllStandards] = useState({});
  const [selectedSubject, setSelectedSubject] = useState('');
  const [selectedGradeBand, setSelectedGradeBand] = useState('');
  const [quizStandards, setQuizStandards] = useState([]);
  const [savingStandards, setSavingStandards] = useState(false);
  
  // Passage visibility state
  const [showPassageToStudents, setShowPassageToStudents] = useState(false);
  const [savingPassageSetting, setSavingPassageSetting] = useState(false);

  useEffect(() => {
    fetchQuiz();
    fetchStandards();
  }, [quizId]);

  const fetchStandards = async () => {
    try {
      const response = await axios.get(`${API}/standards`);
      setAllStandards(response.data.standards);
    } catch (error) {
      console.error('Error fetching standards:', error);
    }
  };

  const fetchQuiz = async () => {
    try {
      const response = await axios.get(`${API}/quizzes/${quizId}`, {
        headers: getAuthHeader()
      });
      setQuiz(response.data);
      setQuizStandards(response.data.standards || []);
      setShowPassageToStudents(response.data.show_passage_to_students || false);

      // Fetch lesson
      const lessonResponse = await axios.get(`${API}/lessons/${response.data.lesson_id}`, {
        headers: getAuthHeader()
      });
      setLesson(lessonResponse.data);
    } catch (error) {
      console.error('Error fetching quiz:', error);
      toast.error('Failed to load quiz');
      navigate('/library');
    } finally {
      setLoading(false);
    }
  };

  const handleEditQuestion = (index) => {
    setEditingIndex(index);
    setEditForm({ ...quiz.questions[index] });
  };

  const handleSaveEdit = async () => {
    try {
      await axios.put(
        `${API}/quizzes/${quizId}/questions/${editingIndex}`,
        editForm,
        { headers: getAuthHeader() }
      );
      
      const updatedQuestions = [...quiz.questions];
      updatedQuestions[editingIndex] = editForm;
      setQuiz({ ...quiz, questions: updatedQuestions });
      setEditingIndex(null);
      toast.success('Question updated');
    } catch (error) {
      console.error('Error updating question:', error);
      toast.error('Failed to update question');
    }
  };

  const handleDeleteQuestion = async (index) => {
    if (!confirm('Are you sure you want to delete this question?')) return;

    try {
      await axios.delete(
        `${API}/quizzes/${quizId}/questions/${index}`,
        { headers: getAuthHeader() }
      );
      
      const updatedQuestions = quiz.questions.filter((_, i) => i !== index);
      setQuiz({ ...quiz, questions: updatedQuestions });
      toast.success('Question deleted');
    } catch (error) {
      console.error('Error deleting question:', error);
      toast.error('Failed to delete question');
    }
  };

  const handleRegenerateQuestion = async (index) => {
    setRegeneratingIndex(index);
    try {
      const response = await axios.post(
        `${API}/quizzes/${quizId}/questions/${index}/regenerate`,
        {},
        { headers: getAuthHeader() }
      );
      
      const updatedQuestions = [...quiz.questions];
      updatedQuestions[index] = response.data;
      setQuiz({ ...quiz, questions: updatedQuestions });
      toast.success('Question regenerated');
    } catch (error) {
      console.error('Error regenerating question:', error);
      toast.error('Failed to regenerate question');
    } finally {
      setRegeneratingIndex(null);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await axios.get(
        `${API}/quizzes/${quizId}/export/${exportOptions.format}?include_answers=${exportOptions.includeAnswers}&include_explanations=${exportOptions.includeExplanations}&include_passage=${exportOptions.includePassage}`,
        {
          headers: getAuthHeader(),
          responseType: 'blob'
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${quiz.title.replace(/\s+/g, '_')}_quiz.${exportOptions.format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success(`Quiz exported as ${exportOptions.format.toUpperCase()}`);
      setShowExportModal(false);
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export quiz');
    } finally {
      setExporting(false);
    }
  };

  const handleDuplicate = async () => {
    try {
      const response = await axios.post(
        `${API}/quizzes/${quizId}/duplicate`,
        {},
        { headers: getAuthHeader() }
      );
      toast.success('Quiz duplicated');
      navigate(`/quiz/${response.data.id}`);
    } catch (error) {
      console.error('Duplicate error:', error);
      toast.error('Failed to duplicate quiz');
    }
  };

  const handleSaveAsTemplate = async () => {
    if (!templateForm.name.trim()) {
      toast.error('Please enter a template name');
      return;
    }
    
    setSavingTemplate(true);
    try {
      await axios.post(
        `${API}/templates/save-from-quiz/${quizId}`,
        null,
        { 
          headers: getAuthHeader(),
          params: {
            name: templateForm.name,
            description: templateForm.description || `Custom template based on ${quiz?.title}`
          }
        }
      );
      toast.success('Saved as template! Find it in Create Quiz > Templates');
      setShowSaveTemplateModal(false);
      setTemplateForm({ name: '', description: '' });
    } catch (error) {
      console.error('Save template error:', error);
      toast.error(error.response?.data?.detail || 'Failed to save as template');
    } finally {
      setSavingTemplate(false);
    }
  };

  const addStandard = (standardCode) => {
    if (!quizStandards.includes(standardCode)) {
      setQuizStandards([...quizStandards, standardCode]);
    }
  };

  const removeStandard = (standardCode) => {
    setQuizStandards(quizStandards.filter(s => s !== standardCode));
  };

  const saveStandards = async () => {
    setSavingStandards(true);
    try {
      await axios.put(
        `${API}/quizzes/${quizId}/standards`,
        quizStandards,
        { headers: getAuthHeader() }
      );
      setQuiz({ ...quiz, standards: quizStandards });
      toast.success('Standards updated!');
      setShowStandardsModal(false);
    } catch (error) {
      console.error('Save standards error:', error);
      toast.error('Failed to save standards');
    } finally {
      setSavingStandards(false);
    }
  };

  const handleTogglePassageVisibility = async (checked) => {
    setSavingPassageSetting(true);
    try {
      await axios.put(
        `${API}/quizzes/${quizId}`,
        { show_passage_to_students: checked },
        { headers: getAuthHeader() }
      );
      setShowPassageToStudents(checked);
      setQuiz({ ...quiz, show_passage_to_students: checked });
      toast.success(checked ? 'Passage will be visible to students' : 'Passage hidden from students');
    } catch (error) {
      console.error('Update passage visibility error:', error);
      toast.error('Failed to update setting');
    } finally {
      setSavingPassageSetting(false);
    }
  };

  const getStandardInfo = (code) => {
    for (const [subj, data] of Object.entries(allStandards)) {
      for (const [grade, standards] of Object.entries(data.grades || {})) {
        const found = standards.find(s => s.code === code);
        if (found) return { ...found, subject: subj, grade };
      }
    }
    return null;
  };

  const getQuizGradeBand = () => {
    const grade = quiz?.grade_level || '';
    if (grade.includes('K') || grade.includes('1st') || grade.includes('2nd')) return 'K-2';
    if (grade.includes('3rd') || grade.includes('4th') || grade.includes('5th')) return '3-5';
    if (grade.includes('6th') || grade.includes('7th') || grade.includes('8th')) return '6-8';
    return '9-12';
  };

  const getQuizSubjectKey = () => {
    const subj = (quiz?.subject || '').toLowerCase();
    if (subj.includes('math')) return 'math';
    if (subj.includes('science') || subj.includes('bio') || subj.includes('chem') || subj.includes('phys')) return 'science';
    if (subj.includes('history') || subj.includes('social') || subj.includes('geo')) return 'history';
    return 'ela';
  };

  const getQuestionTypeLabel = (type) => {
    const labels = {
      multiple_choice: 'Multiple Choice',
      true_false: 'True/False',
      short_answer: 'Short Answer',
      fill_in_blank: 'Fill in the Blank'
    };
    return labels[type] || type;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Skeleton className="h-10 w-64 mb-6" />
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Skeleton className="h-[600px] lg:col-span-1" />
            <Skeleton className="h-[600px] lg:col-span-2" />
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8"
        >
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/library')}
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="font-serif text-2xl md:text-3xl font-medium text-primary">
                {quiz?.title}
              </h1>
              <p className="text-muted-foreground">
                {quiz?.subject} • {quiz?.grade_level} • {quiz?.questions.length} questions
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={() => {
                setTemplateForm({
                  name: `${quiz?.title} Template`,
                  description: `Custom template based on ${quiz?.title}`
                });
                setShowSaveTemplateModal(true);
              }}
              className="gap-2"
              data-testid="save-template-btn"
            >
              <BookmarkPlus className="w-4 h-4" />
              Save as Template
            </Button>
            <Button
              variant="outline"
              onClick={handleDuplicate}
              className="gap-2"
              data-testid="duplicate-btn"
            >
              <Copy className="w-4 h-4" />
              Duplicate
            </Button>
            <Button
              onClick={() => setShowExportModal(true)}
              className="gap-2 bg-primary hover:bg-primary/90"
              data-testid="export-btn"
            >
              <Download className="w-4 h-4" />
              Export
            </Button>
          </div>
        </motion.div>

        {/* Standards Alignment Section */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >
          <Card className="border-border bg-secondary/20">
            <CardContent className="py-4 px-5">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-2">
                  <GraduationCap className="w-5 h-5 text-primary" />
                  <span className="font-medium text-sm">Standards Alignment</span>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  {quizStandards.length > 0 ? (
                    quizStandards.map(code => {
                      const info = getStandardInfo(code);
                      return (
                        <Badge 
                          key={code} 
                          variant="secondary" 
                          className="text-xs cursor-help"
                          title={info?.description || code}
                        >
                          {code}
                        </Badge>
                      );
                    })
                  ) : (
                    <span className="text-sm text-muted-foreground">No standards aligned yet</span>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-1 h-7"
                    onClick={() => {
                      setSelectedSubject(getQuizSubjectKey());
                      setSelectedGradeBand(getQuizGradeBand());
                      setShowStandardsModal(true);
                    }}
                    data-testid="add-standards-btn"
                  >
                    <Plus className="w-3 h-3" />
                    {quizStandards.length > 0 ? 'Edit' : 'Add Standards'}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Student Passage Visibility Toggle */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="mb-6"
        >
          <Card className="border-border bg-secondary/20">
            <CardContent className="py-4 px-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {showPassageToStudents ? (
                    <Eye className="w-5 h-5 text-primary" />
                  ) : (
                    <EyeOff className="w-5 h-5 text-muted-foreground" />
                  )}
                  <div>
                    <span className="font-medium text-sm">Show Reading Passage to Students</span>
                    <p className="text-xs text-muted-foreground">
                      {showPassageToStudents 
                        ? 'Students can view the passage before and during the quiz'
                        : 'Passage is hidden from students'}
                    </p>
                  </div>
                </div>
                <Switch
                  checked={showPassageToStudents}
                  onCheckedChange={handleTogglePassageVisibility}
                  disabled={savingPassageSetting}
                  data-testid="show-passage-toggle"
                />
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Passage */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
            className="lg:col-span-1"
          >
            <Card className="border-border sticky top-24">
              <CardHeader className="pb-3">
                <CardTitle className="font-serif text-lg flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Lesson Passage
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[500px] pr-4">
                  <PassageReader text={lesson?.passage_text} />
                </ScrollArea>
              </CardContent>
            </Card>
          </motion.div>

          {/* Right Column - Questions */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            className="lg:col-span-2 space-y-4"
          >
            {quiz?.questions.map((question, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 * index }}
              >
                <Card
                  className={`border-border hover:border-primary/30 transition-colors ${
                    regeneratingIndex === index ? 'opacity-50' : ''
                  }`}
                  data-testid={`question-card-${index}`}
                >
                  <CardContent className="p-6">
                    {/* Question Header */}
                    <div className="flex items-start justify-between gap-4 mb-4">
                      <div className="flex items-center gap-3">
                        <span className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center text-sm font-semibold text-primary">
                          {index + 1}
                        </span>
                        <span className="text-xs font-medium px-2 py-1 bg-secondary rounded-full text-muted-foreground">
                          {getQuestionTypeLabel(question.question_type)}
                        </span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleEditQuestion(index)}
                          disabled={regeneratingIndex === index}
                          data-testid={`edit-question-${index}`}
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleRegenerateQuestion(index)}
                          disabled={regeneratingIndex === index}
                          data-testid={`regenerate-question-${index}`}
                        >
                          {regeneratingIndex === index ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <RefreshCw className="w-4 h-4" />
                          )}
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDeleteQuestion(index)}
                          disabled={regeneratingIndex === index}
                          className="hover:text-destructive"
                          data-testid={`delete-question-${index}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>

                    {/* Question Text */}
                    <div className="text-foreground font-medium mb-4">
                      <MathText>{question.question}</MathText>
                    </div>
                    {question.image_url && (
                      <img
                        src={question.image_url}
                        alt="Question diagram"
                        className="mb-4 max-h-56 rounded-lg border border-border"
                      />
                    )}

                    {/* Options for Multiple Choice */}
                    {question.question_type === 'multiple_choice' && question.options && (
                      <div className="space-y-2 mb-4">
                        {question.options.map((option, optIndex) => (
                          <div
                            key={optIndex}
                            className={`flex items-center gap-3 p-3 rounded-lg border ${
                              option === question.correct_answer
                                ? 'border-primary/40 bg-primary/5'
                                : 'border-border'
                            }`}
                          >
                            <span className="w-6 h-6 rounded-full bg-secondary flex items-center justify-center text-xs font-medium">
                              {String.fromCharCode(65 + optIndex)}
                            </span>
                            <span className="text-sm"><MathText>{option}</MathText></span>
                            {option === question.correct_answer && (
                              <CheckCircle2 className="w-4 h-4 text-primary ml-auto" />
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* True/False Options */}
                    {question.question_type === 'true_false' && (
                      <div className="flex gap-4 mb-4">
                        {['True', 'False'].map((opt) => (
                          <div
                            key={opt}
                            className={`flex-1 p-3 rounded-lg border text-center ${
                              question.correct_answer.toLowerCase() === opt.toLowerCase()
                                ? 'border-primary/40 bg-primary/5'
                                : 'border-border'
                            }`}
                          >
                            <span className="text-sm font-medium">{opt}</span>
                            {question.correct_answer.toLowerCase() === opt.toLowerCase() && (
                              <CheckCircle2 className="w-4 h-4 text-primary mx-auto mt-1" />
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Answer for Short Answer / Fill in Blank / Numeric */}
                    {(question.question_type === 'short_answer' || question.question_type === 'fill_in_blank' || question.question_type === 'numeric_fill_in_blank') && (
                      <div className="mb-4 p-3 rounded-lg bg-primary/5 border border-primary/20">
                        <p className="text-xs font-medium text-muted-foreground mb-1">Correct Answer</p>
                        <div className="text-sm text-primary font-medium">
                          <MathText>{question.correct_answer}</MathText>
                          {question.question_type === 'numeric_fill_in_blank' && question.exact_match === false && (
                            <span className="ml-2 text-xs text-muted-foreground">(equivalent numeric forms accepted)</span>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Explanation & Worked Solution & Evidence */}
                    <div className="space-y-3 pt-4 border-t border-border">
                      {question.worked_solution && (
                        <div className="flex items-start gap-2">
                          <Lightbulb className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="text-xs font-medium text-muted-foreground mb-1">Worked Solution</p>
                            <div className="text-sm text-foreground/80 whitespace-pre-wrap">
                              <MathText>{question.worked_solution}</MathText>
                            </div>
                          </div>
                        </div>
                      )}
                      <div className="flex items-start gap-2">
                        <Lightbulb className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-xs font-medium text-muted-foreground mb-1">Explanation</p>
                          <div className="text-sm text-foreground/80"><MathText>{question.explanation}</MathText></div>
                        </div>
                      </div>
                      {question.evidence_line && (
                        <div className="flex items-start gap-2">
                          <Quote className="w-4 h-4 text-muted-foreground flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="text-xs font-medium text-muted-foreground mb-1">Evidence from Passage</p>
                            <p className="text-sm text-foreground/80 italic">"{question.evidence_line}"</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </main>

      {/* Edit Question Modal */}
      <Dialog open={editingIndex !== null} onOpenChange={() => setEditingIndex(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-serif">Edit Question {editingIndex !== null ? editingIndex + 1 : ''}</DialogTitle>
            <DialogDescription>Make changes to this question</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Question</label>
              <Textarea
                value={editForm.question || ''}
                onChange={(e) => setEditForm({ ...editForm, question: e.target.value })}
                className="min-h-[100px]"
                data-testid="edit-question-text"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Correct Answer</label>
              <Input
                value={editForm.correct_answer || ''}
                onChange={(e) => setEditForm({ ...editForm, correct_answer: e.target.value })}
                data-testid="edit-correct-answer"
              />
            </div>

            {editForm.question_type === 'multiple_choice' && editForm.options && (
              <div className="space-y-2">
                <label className="text-sm font-medium">Options</label>
                {editForm.options.map((opt, i) => (
                  <Input
                    key={i}
                    value={opt}
                    onChange={(e) => {
                      const newOptions = [...editForm.options];
                      newOptions[i] = e.target.value;
                      setEditForm({ ...editForm, options: newOptions });
                    }}
                    placeholder={`Option ${String.fromCharCode(65 + i)}`}
                    data-testid={`edit-option-${i}`}
                  />
                ))}
              </div>
            )}

            <div className="space-y-2">
              <label className="text-sm font-medium">Explanation</label>
              <Textarea
                value={editForm.explanation || ''}
                onChange={(e) => setEditForm({ ...editForm, explanation: e.target.value })}
                data-testid="edit-explanation"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Evidence Quote</label>
              <Textarea
                value={editForm.evidence_line || ''}
                onChange={(e) => setEditForm({ ...editForm, evidence_line: e.target.value })}
                data-testid="edit-evidence"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingIndex(null)}>
              Cancel
            </Button>
            <Button onClick={handleSaveEdit} data-testid="save-edit-btn">
              <Save className="w-4 h-4 mr-2" />
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Export Modal */}
      <Dialog open={showExportModal} onOpenChange={setShowExportModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-serif">Export Quiz</DialogTitle>
            <DialogDescription>Choose your export options</DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            <div className="space-y-3">
              <label className="text-sm font-medium">Format</label>
              <div className="flex gap-3">
                {['pdf', 'docx'].map((format) => (
                  <Button
                    key={format}
                    variant={exportOptions.format === format ? 'default' : 'outline'}
                    className={`flex-1 uppercase ${exportOptions.format === format ? 'bg-primary' : ''}`}
                    onClick={() => setExportOptions({ ...exportOptions, format })}
                    data-testid={`export-format-${format}`}
                  >
                    {format}
                  </Button>
                ))}
              </div>
            </div>

            <div className="space-y-3">
              <label className="text-sm font-medium">Include</label>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <Checkbox
                    id="includePassage"
                    checked={exportOptions.includePassage}
                    onCheckedChange={(checked) =>
                      setExportOptions({ ...exportOptions, includePassage: checked })
                    }
                    data-testid="export-include-passage"
                  />
                  <label htmlFor="includePassage" className="text-sm">
                    Reading Passage (first page)
                  </label>
                </div>
                <div className="flex items-center gap-3">
                  <Checkbox
                    id="includeAnswers"
                    checked={exportOptions.includeAnswers}
                    onCheckedChange={(checked) =>
                      setExportOptions({ ...exportOptions, includeAnswers: checked })
                    }
                    data-testid="export-include-answers"
                  />
                  <label htmlFor="includeAnswers" className="text-sm">
                    Answer Key (separate page)
                  </label>
                </div>
                <div className="flex items-center gap-3">
                  <Checkbox
                    id="includeExplanations"
                    checked={exportOptions.includeExplanations}
                    onCheckedChange={(checked) =>
                      setExportOptions({ ...exportOptions, includeExplanations: checked })
                    }
                    disabled={!exportOptions.includeAnswers}
                    data-testid="export-include-explanations"
                  />
                  <label
                    htmlFor="includeExplanations"
                    className={`text-sm ${!exportOptions.includeAnswers ? 'text-muted-foreground' : ''}`}
                  >
                    Explanations & Evidence
                  </label>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowExportModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleExport} disabled={exporting} data-testid="confirm-export-btn">
              {exporting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Exporting...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4 mr-2" />
                  Download
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Save as Template Modal */}
      <Dialog open={showSaveTemplateModal} onOpenChange={setShowSaveTemplateModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-serif flex items-center gap-2">
              <BookmarkPlus className="w-5 h-5" />
              Save as Template
            </DialogTitle>
            <DialogDescription>
              Create a reusable template from this quiz
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="templateName">Template Name</Label>
              <Input
                id="templateName"
                value={templateForm.name}
                onChange={(e) => setTemplateForm({ ...templateForm, name: e.target.value })}
                placeholder="e.g., Water Cycle Quiz Template"
                data-testid="template-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="templateDescription">Description (optional)</Label>
              <Textarea
                id="templateDescription"
                value={templateForm.description}
                onChange={(e) => setTemplateForm({ ...templateForm, description: e.target.value })}
                placeholder="Brief description of what this template covers..."
                rows={3}
                data-testid="template-description-input"
              />
            </div>
            <div className="bg-secondary/50 p-4 rounded-lg">
              <p className="text-sm text-muted-foreground">
                This will save the lesson content and quiz settings as a template. 
                You'll find it in <strong>Create Quiz → Templates → My Templates</strong>.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSaveTemplateModal(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSaveAsTemplate} 
              disabled={savingTemplate || !templateForm.name.trim()}
              data-testid="confirm-save-template-btn"
            >
              {savingTemplate ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <BookmarkPlus className="w-4 h-4 mr-2" />
                  Save Template
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Standards Alignment Modal */}
      <Dialog open={showStandardsModal} onOpenChange={setShowStandardsModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-serif flex items-center gap-2">
              <GraduationCap className="w-5 h-5" />
              Align with Standards
            </DialogTitle>
            <DialogDescription>
              Select Common Core or NGSS standards that this quiz addresses
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Selected Standards */}
            {quizStandards.length > 0 && (
              <div className="space-y-2">
                <Label>Selected Standards</Label>
                <div className="flex flex-wrap gap-2 p-3 bg-secondary/30 rounded-lg min-h-[60px]">
                  {quizStandards.map(code => {
                    const info = getStandardInfo(code);
                    return (
                      <Badge 
                        key={code} 
                        variant="default"
                        className="gap-1 pr-1"
                      >
                        {code}
                        <button
                          onClick={() => removeStandard(code)}
                          className="ml-1 hover:bg-primary-foreground/20 rounded-full p-0.5"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </Badge>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Subject and Grade Selection */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Subject Area</Label>
                <Select value={selectedSubject} onValueChange={setSelectedSubject}>
                  <SelectTrigger data-testid="standards-subject-select">
                    <SelectValue placeholder="Select subject" />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(allStandards).map(([key, data]) => (
                      <SelectItem key={key} value={key}>{data.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Grade Band</Label>
                <Select value={selectedGradeBand} onValueChange={setSelectedGradeBand}>
                  <SelectTrigger data-testid="standards-grade-select">
                    <SelectValue placeholder="Select grade" />
                  </SelectTrigger>
                  <SelectContent>
                    {selectedSubject && allStandards[selectedSubject]?.grades && 
                      Object.keys(allStandards[selectedSubject].grades).map(grade => (
                        <SelectItem key={grade} value={grade}>{grade}</SelectItem>
                      ))
                    }
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Available Standards */}
            {selectedSubject && selectedGradeBand && allStandards[selectedSubject]?.grades?.[selectedGradeBand] && (
              <div className="space-y-2">
                <Label>Available Standards</Label>
                <ScrollArea className="h-[250px] border rounded-lg">
                  <div className="p-3 space-y-2">
                    {allStandards[selectedSubject].grades[selectedGradeBand].map((standard) => (
                      <div
                        key={standard.code}
                        className={`p-3 rounded-lg border cursor-pointer transition-all ${
                          quizStandards.includes(standard.code)
                            ? 'border-primary bg-primary/5'
                            : 'border-border hover:border-primary/50 hover:bg-secondary/30'
                        }`}
                        onClick={() => {
                          if (quizStandards.includes(standard.code)) {
                            removeStandard(standard.code);
                          } else {
                            addStandard(standard.code);
                          }
                        }}
                        data-testid={`standard-${standard.code}`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <Badge variant="outline" className="text-xs">{standard.category}</Badge>
                              <code className="text-xs font-mono text-primary">{standard.code}</code>
                            </div>
                            <p className="text-sm text-muted-foreground">{standard.description}</p>
                          </div>
                          {quizStandards.includes(standard.code) && (
                            <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0" />
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowStandardsModal(false)}>
              Cancel
            </Button>
            <Button 
              onClick={saveStandards} 
              disabled={savingStandards}
              data-testid="save-standards-btn"
            >
              {savingStandards ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Save Standards
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default QuizReviewPage;
